"use client";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Code,
  ExternalLink,
  Image,
  Play,
  Package,
  Download,
  Check,
  Box,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import type { EnhancedBlockConfig } from "@/app/(app)/components/_utils/component-data";
import { ComponentBreadcrumb } from "@/app/(app)/components/_components/component-breadcrumb";
import { getLibraryStyles } from "@/lib/colors/library-colors";
import { CopyButton } from "@/components/ui/copy-button";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState } from "react";

interface FileMeta {
  name?: string;
  source?: string;
  content?: string;
}

interface ComponentLayoutProps {
  component: EnhancedBlockConfig;
  children: ReactNode;
}

export default function ComponentLayout({
  children,
  params,
}: {
  children: ReactNode;
  params: { library: string; component: string };
}) {
  return <div className="relative min-h-screen flex flex-col">{children}</div>;
}

function FloatingDetailsPanel({
  component,
}: {
  component: EnhancedBlockConfig;
}) {
  const [copySuccess, setCopySuccess] = useState(false);
  const libraryStyles = getLibraryStyles(component.library || "");

  const firstFile = component.files?.[0] as FileMeta | undefined;
  const codeContentToCopy = firstFile?.content ?? "";
  const codeFilenameToDownload =
    firstFile?.source ||
    firstFile?.name ||
    `${component.name || "component"}.tsx`;

  const handleDownload = () => {
    if (!codeContentToCopy) return;
    const blob = new Blob([codeContentToCopy], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = codeFilenameToDownload;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopySource = async () => {
    if (!codeContentToCopy) return;
    try {
      await navigator.clipboard.writeText(codeContentToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy source code: ", err);
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 w-full max-w-sm rounded-lg border bg-card p-4 shadow-lg text-card-foreground">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          {component.library && (
            <Badge
              variant={"outline"}
              className="rounded-md text-xs font-medium px-2 py-0.5 border"
              style={{ borderColor: libraryStyles.borderColor }}
            >
              {component.library}
            </Badge>
          )}
          <div className="h-4 w-px bg-border" />
          <div className="flex gap-1 flex-wrap">
            {(component.categories || []).map((category) => (
              <Badge
                key={category}
                variant="secondary"
                className="rounded-full text-xs font-normal px-2 py-0"
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>

        <h1 className="text-xl font-semibold tracking-tight">
          {component.name}
        </h1>

        <p className="text-sm text-muted-foreground leading-relaxed">
          {component.description || "No description available"}
        </p>

        {component.tags && component.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {component.tags.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="rounded-full text-xs font-normal px-2 py-0"
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(component.shadcn?.command || component.install) && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Package className="mr-1.5 h-3.5 w-3.5" />
                Install
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2 space-y-2">
              <div className="flex items-center space-x-2">
                <Input
                  readOnly
                  value={component.shadcn?.command || component.install || ""}
                  className="h-8 flex-1 font-mono text-xs"
                  aria-label="Install command"
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CopyButton
                        value={
                          component.shadcn?.command || component.install || ""
                        }
                        successTitle="Command copied"
                        successDescription="Install command copied to clipboard"
                        className="h-8 w-8 p-0"
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy install command</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {codeContentToCopy && (
                <div className="mt-2 pt-2 border-t">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="flex items-center gap-2 cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={handleCopySource}
                          role="menuitem"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              handleCopySource();
                          }}
                        >
                          {copySuccess ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Code className="h-4 w-4" />
                          )}
                          <span>{copySuccess ? "Copied!" : "Copy Source"}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Copy component source code</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="flex items-center gap-2 cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={handleDownload}
                          role="menuitem"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              handleDownload();
                          }}
                        >
                          <Download className="h-4 w-4" />
                          <span>Download File</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Download component file ({codeFilenameToDownload})
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}

        {component.docsUrl && (
          <Link
            href={component.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            <span>Docs</span>
          </Link>
        )}
      </div>
    </div>
  );
}

export function ComponentTabs({ component, children }: ComponentLayoutProps) {
  const pathname = usePathname();
  const basePath = `/components/${component.library}/${component.name}`;

  const tabs = [
    { id: "", label: "Preview", icon: Play, path: basePath },
    { id: "code", label: "Code", icon: Code, path: `${basePath}/code` },
    {
      id: "sandbox",
      label: "Sandbox",
      icon: Box,
      path: `${basePath}/sandpack`,
      disabled: !(component.files && component.files.length > 0),
    },
    {
      id: "images",
      label: "Images",
      icon: Image,
      path: `${basePath}/images`,
      disabled: !(component.files && component.files.length > 0),
    },
  ];

  const activeTab =
    pathname === basePath
      ? ""
      : pathname.startsWith(`${basePath}/`)
        ? pathname.substring(basePath.length + 1).split("/")[0]
        : "";

  return (
    <div className="flex-grow flex flex-col">
      <div className="container max-w-screen-xl mx-auto px-4 pt-4 sticky top-0 z-40 bg-background/80 backdrop-blur-sm pb-2">
        <ComponentBreadcrumb component={component} currentTab={activeTab} />
      </div>

      <FloatingDetailsPanel component={component} />

      <div className="border-b sticky top-[calc(2.5rem+8px)] z-30 bg-background/80 backdrop-blur-sm">
        <div className="container max-w-screen-xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto">
            {tabs.map((tab) => (
              <Link
                key={tab.id}
                href={tab.path}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 border-transparent whitespace-nowrap",
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground",
                  tab.disabled && "opacity-50 pointer-events-none"
                )}
                aria-disabled={tab.disabled}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.id === "images" &&
                  component.files &&
                  component.files.length > 0 && (
                    <span className="ml-1 text-xs rounded-full bg-muted px-1.5 py-0.5">
                      {component.files?.length ?? 0}
                    </span>
                  )}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="container max-w-screen-xl mx-auto px-4 py-6 flex-grow">
        {children}
      </div>
    </div>
  );
}
